/**
 * SkillsDoMembro — skills efetivas do membro do squad (SPEC A2 FR-15, AG-14).
 *
 *   - `skills = null` no design herda o catálogo; uma lista SUBSTITUI no projeto;
 *   - acrescentar skill do catálogo (busca, como no Catálogo);
 *   - remover NO PROJETO (grava a lista explícita — o catálogo do tenant não muda);
 *   - "Voltar ao padrão do catálogo" (`skills = null`);
 *   - skill sem conteúdo aparece com ⚠️ e o link "Abrir no Catálogo";
 *   - na escolha, skills COM conteúdo vêm primeiro; as sem conteúdo ficam por último com
 *     "⚠️ sem conteúdo".
 */
import { useMemo, useState } from 'react';
import { Plus, X, RotateCcw } from 'lucide-react';
import { C } from '@/theme/palette';
import { useSkillsDoAgente } from '@/api/agents';
import { useSkills } from '@/api/skills';
import { itensDe, ordenarSkillsPorConteudo } from './comum';

/**
 * @param {{
 *   agent: string,
 *   skills: string[]|null|undefined,        // valor no design (null = padrão do catálogo)
 *   conferidas?: Array<{name, tem_conteudo, origem}>,  // conferencia.membros[].skills
 *   onChange: (skills: string[]|null) => void,
 * }} props
 */
export function SkillsDoMembro({ agent, skills, conferidas = [], onChange }) {
  const doCatalogoQ = useSkillsDoAgente(agent);
  const [busca, setBusca] = useState('');
  const [escolhida, setEscolhida] = useState('');
  const buscaQ = useSkills({ search: busca.trim() || undefined, limit: 50 });

  const doCatalogo = useMemo(() => itensDe(doCatalogoQ.data).filter((s) => s.name), [doCatalogoQ.data]);
  const explicita = Array.isArray(skills);

  // Lista efetiva: a do projeto (explícita) ou a do catálogo.
  const efetivas = useMemo(() => {
    if (explicita) return skills;
    const daConferencia = (conferidas || []).filter((s) => s?.origem === 'catalogo').map((s) => s.name);
    return doCatalogo.length ? doCatalogo.map((s) => s.name) : daConferencia;
  }, [explicita, skills, conferidas, doCatalogo]);

  // `tem_conteudo` vem da conferência, do catálogo do agente ou da busca de skills.
  const temConteudo = useMemo(() => {
    const m = new Map();
    for (const s of itensDe(buscaQ.data)) if (typeof s.tem_conteudo === 'boolean') m.set(s.name, s.tem_conteudo);
    for (const s of doCatalogo) if (typeof s.tem_conteudo === 'boolean') m.set(s.name, s.tem_conteudo);
    for (const s of conferidas || []) if (typeof s?.tem_conteudo === 'boolean') m.set(s.name, s.tem_conteudo);
    return m;
  }, [buscaQ.data, doCatalogo, conferidas]);

  const opcoes = ordenarSkillsPorConteudo(
    itensDe(buscaQ.data).map((s) => s.name).filter((n) => n && !efetivas.includes(n)),
    (n) => temConteudo.get(n),
  );

  const remover = (nome) => onChange(efetivas.filter((n) => n !== nome));
  const adicionar = () => {
    if (!escolhida) return;
    onChange([...efetivas.filter((n) => n !== escolhida), escolhida]);
    setEscolhida('');
  };

  return (
    <div role="group" aria-label={`Skills de ${agent}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 600, color: C.neutralMuted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Skills ({efetivas.length})
        </span>
        <span data-testid="origem-skills" style={{ fontSize: 10.5, color: explicita ? C.warn : C.neutralMuted }}>
          {explicita ? 'lista do projeto' : 'padrão do catálogo'}
        </span>
      </div>

      {efetivas.length === 0 && (
        <div style={{ fontSize: 12, color: C.neutralDim, marginBottom: 6 }}>
          {doCatalogoQ.isLoading && !explicita ? 'Carregando skills…' : 'Nenhuma skill.'}
        </div>
      )}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}>
        {efetivas.map((nome) => {
          const semConteudo = temConteudo.get(nome) === false;
          return (
            <li key={nome} data-testid={`skill-${nome}`}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.neutral }}>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {semConteudo ? '⚠️ ' : ''}{nome}
              </span>
              {semConteudo && (
                <a href={`/catalog?tab=skills&search=${encodeURIComponent(nome)}`}
                  target="_blank" rel="noreferrer"
                  title="Esta skill não tem conteúdo — abra no Catálogo para completar"
                  style={{ fontSize: 11, color: C.warn }}>
                  sem conteúdo · Abrir no Catálogo
                </a>
              )}
              <button type="button" onClick={() => remover(nome)} aria-label={`Remover skill ${nome} no projeto`}
                style={{ background: 'none', border: 'none', color: C.neutralMuted, cursor: 'pointer', padding: 2 }}>
                <X size={12} />
              </button>
            </li>
          );
        })}
      </ul>

      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
        <input value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar skill no catálogo"
          placeholder="Buscar skill no catálogo…"
          style={{ flex: '1 1 100%', background: C.black, color: C.neutral, border: `1px solid ${C.blackBorder}`,
            borderRadius: 6, padding: '6px 10px', fontSize: 12 }} />
        <select value={escolhida} onChange={(e) => setEscolhida(e.target.value)} aria-label="Skill para acrescentar"
          style={{ flex: 1, minWidth: 0, background: C.black, color: C.neutral, border: `1px solid ${C.blackBorder}`,
            borderRadius: 6, padding: '6px 8px', fontSize: 12 }}>
          <option value="">Selecione uma skill…</option>
          {opcoes.map((n) => (
            <option key={n} value={n} data-sem-conteudo={temConteudo.get(n) === false ? 'true' : undefined}>
              {n}{temConteudo.get(n) === false ? ' — ⚠️ sem conteúdo' : ''}
            </option>
          ))}
        </select>
        <button type="button" onClick={adicionar} disabled={!escolhida}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '5px 10px',
            borderRadius: 6, background: 'transparent', color: C.cyber, border: `1px solid ${C.cyber}`,
            opacity: escolhida ? 1 : 0.4, cursor: escolhida ? 'pointer' : 'not-allowed' }}>
          <Plus size={12} /> Acrescentar
        </button>
      </div>

      {explicita && (
        <button type="button" onClick={() => onChange(null)}
          style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5,
            background: 'none', border: 'none', color: C.cyber, cursor: 'pointer', padding: 0 }}>
          <RotateCcw size={12} /> Voltar ao padrão do catálogo
        </button>
      )}
    </div>
  );
}
